import {
  apiGroupDetail,
  getMachineInfo,
  getMachineReports,
  getUser,
} from "@app/api";
import { ResponseStatus, StorageKey } from "@app/constants";
import { MachineInfo, MachineOpen } from "@app/pages/Features";
import { Machine, MachineInfoType } from "@app/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useNavigate, Outlet } from "react-router-dom";
import { Tabs, Tab, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Helmet } from "@app/components/atoms";
import { useDispatch, useSelector } from "react-redux";
import {
  RootState,
  saveScreenPermission,
  setIdReport,
  setIsMachinePage,
  setIsRequestedMachineInfo,
  setReportType,
} from "@app/store";
import {
  clearMachineReportsPreferences,
  isMobile,
  resetIsContinuousLoading,
  setItem,
} from "@app/helpers";
import { Footer } from "@app/components/organisms";
import { useAppContext } from "@app/AppContext";

interface TabPanelProps {
  label: string;
  route: string;
  disabled?: boolean;
  className?: string;
}

export function MachineDetail() {
  const { t } = useTranslation();
  const { groupId, machineId } = useParams();
  const {
    setGroupName: setCtxGroupName,
    setMachineName: setCtxMachineName,
    setMachineModelType,
  } = useAppContext();
  const [machineInfo, setMachineInfo] = useState<MachineInfoType | undefined>();
  const [groupNameLocal, setGroupNameLocal] = useState<string>("");
  const [machineReportsOpen, setMachineReportsOpen] = useState<Machine[]>();
  const navigate = useNavigate();
  const [metaTitle, setMetaTitle] = useState<string>("");
  const [isRequestedReports, setIsRequestedReports] = useState(false);
  const [isRequestedGroupDetails, setIsRequestedGroupDetails] = useState(false);
  const { isUpdatedMachineReportResponses } = useSelector(
    (state: RootState) => state.inspection,
  );
  const dispatch = useDispatch();
  const [bodyHeight, setBodyHeight] = useState(0);
  const [currentBodyHeight, setCurrentBodyHeight] = useState(0);
  const [localeCode, setLocaleCode] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const tabPanels: TabPanelProps[] = [
    {
      label: t("labelInspection"),
      route: `/groups/${groupId}/machines/${machineId}/inspection-history`,
      className: `tab-item `,
    },
    {
      label: t("groupDetailTab.reportTab"),
      route: `/groups/${groupId}/machines/${machineId}/report`,
      className: "tab-item",
    },
  ];

  const currentRoute = useMemo(() => {
    let current = window.location.pathname;

    if (!current.includes("report")) {
      current = `/groups/${groupId}/machines/${machineId}/inspection-history`;
    }

    return current;
  }, [groupId, machineId]);
  const [valueRoute, setValueRoute] = useState(currentRoute);

  const handleChange = (_: React.SyntheticEvent, newValueRoute: string) => {
    setValueRoute(newValueRoute);
  };

  useEffect(() => {
    clearMachineReportsPreferences();
    // Cleanup: Clear machine name when leaving this component
    return () => {
      setCtxMachineName("");
      setMachineModelType("");
    };
  }, [setCtxMachineName, setMachineModelType]);

  const onBack = () => {
    navigate(`/groups/${groupId}`);
  };

  const getGroupDetail = (id: string) => {
    return apiGroupDetail(id).then((db) => {
      const { data, meta } = db.data;
      setCtxGroupName(data?.groupName || "");
      setGroupNameLocal(data?.groupName);
      if (meta?.screenPermission) {
        dispatch(saveScreenPermission(meta.screenPermission));
      }
    });
  };

  const convertDataRow = useCallback(
    (data: Machine[]) => {
      return data.map((row: any) => ({
        ...row,
        id: row?.machineReportId,
        reporterName:
          localeCode === "ja"
            ? `${row?.surname} ${row?.givenName}`
            : `${row?.givenName} ${row?.surname}`,
      }));
    },
    [localeCode],
  );

  const getMachineInfoPage = useCallback(
    (groupId: string, machineId: string) => {
      setIsLoading(true);
      getMachineReports(groupId, machineId, {
        responseStatus: ResponseStatus.UNADDRESSED,
      })
        .then((db: { data: Machine[] }) => {
          setMachineReportsOpen(convertDataRow(db?.data || []));
        })
        .finally(() => {
          setIsRequestedReports(true);
          setIsLoading(false);
        });
    },
    [convertDataRow],
  );

  const refreshMachineData = async () => {
    if (!groupId || !machineId) return;

    try {
      const [updatedReports, updatedMachineInfo] = await Promise.all([
        getMachineReports(groupId, machineId, {
          responseStatus: ResponseStatus.UNADDRESSED,
        }),
        getMachineInfo(groupId, machineId),
      ]);

      setMachineReportsOpen(convertDataRow(updatedReports?.data || []));

      setMachineInfo({
        ...updatedMachineInfo,
        machineManufacturerName: updatedMachineInfo.isOtherMachineManufacturer
          ? updatedMachineInfo.customMachineManufacturerName ||
            updatedMachineInfo.machineManufacturerName
          : updatedMachineInfo.machineManufacturerName,
        groupName: groupNameLocal,
      });
      setCtxMachineName(updatedMachineInfo.machineName || "");
      setMachineModelType(updatedMachineInfo.modelAndType || "");
    } catch (error) {
      console.error("Failed to refresh machine data:", error);
    }
  };

  useEffect(() => {
    if (!!groupId && !!machineId) {
      setItem(StorageKey.IS_CONTINUOUS_LOADING, "true");
      getGroupDetail(groupId).finally(() => {
        setIsRequestedGroupDetails(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, machineId]);

  useEffect(() => {
    if (
      groupId &&
      machineId &&
      isRequestedGroupDetails &&
      isUpdatedMachineReportResponses
    ) {
      getMachineInfoPage(groupId, machineId);
    }
  }, [
    groupId,
    machineId,
    isRequestedGroupDetails,
    isUpdatedMachineReportResponses,
  ]);

  useEffect(() => {
    !!isRequestedReports &&
      groupId &&
      machineId &&
      getMachineInfo(groupId, machineId)
        .then((db) => {
          setMachineInfo({
            ...db,
            machineManufacturerName: db.isOtherMachineManufacturer
              ? db.customMachineManufacturerName || db.machineManufacturerName
              : db.machineManufacturerName,
            groupName: groupNameLocal,
          } as MachineInfoType);
          setCtxMachineName(db.machineName || "");
          setMachineModelType(db.modelAndType || "");
        })
        .finally(() => {
          dispatch(setIsRequestedMachineInfo(`${new Date()}`));
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRequestedReports, groupId, machineId, groupNameLocal, dispatch]);

  useEffect(() => {
    if (!!machineInfo) {
      setMetaTitle(
        t(
          `metaInfo.${
            valueRoute.includes("inspection-history")
              ? "machineDetailInspection"
              : "machineDetailReport"
          }`,
          {
            customerMachineNo: machineInfo?.machineName,
          },
        ),
      );
    }

    if (!!bodyHeight) {
      setCurrentBodyHeight(bodyHeight);
    }
  }, [valueRoute, machineInfo, bodyHeight, t]);

  useEffect(() => {
    dispatch(setIsMachinePage(true));
    return () => {
      dispatch(setIsRequestedMachineInfo(""));
      dispatch(setIsMachinePage(false));
      dispatch(setIdReport(""));
      dispatch(setReportType(""));
      resetIsContinuousLoading();
    };
  }, [dispatch]);

  useEffect(() => {
    setValueRoute(currentRoute);
  }, [currentRoute]);

  useEffect(() => {
    setBodyHeight(currentBodyHeight || document.body.offsetHeight);
  }, [currentBodyHeight]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        let timeFormat = response?.data?.data?.timeFormat || "yyyy/MM/dd";
        const isoLocaleCode = response?.data?.data?.isoLocaleCode;

        if (!timeFormat.includes("HH") && !timeFormat.includes("mm")) {
          timeFormat += " HH:mm";
        }

        setLocaleCode(isoLocaleCode);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  return (
    <>
      <div className="machine-detail-page">
        <Helmet title={metaTitle} />
        <div className="container">
          <div className="row flex wrapper-machine-detail">
            <MachineInfo
              machineInfo={machineInfo}
              onBack={onBack}
              refreshMachineInfo={refreshMachineData}
            />

            <div className="machine-detail-right">
              <div
                style={{
                  minHeight: !isMobile()
                    ? `calc(${bodyHeight}px - 130px)`
                    : "auto",
                }}
                className="flex wrapper-machines-and-table"
              >
                {(isLoading || !!machineReportsOpen?.length) && (
                  <MachineOpen
                    dbMachineOpen={machineReportsOpen}
                    isLoading={isLoading}
                  />
                )}

                <div className="machine-tabs">
                  <Box
                    className="data-tabs"
                    sx={{ width: "100%", marginTop: "24px" }}
                  >
                    <Box sx={{ borderBottom: 1, borderColor: "#d0d7de" }}>
                      <Tabs
                        value={valueRoute}
                        variant="scrollable"
                        onChange={handleChange}
                        TabIndicatorProps={{ style: { background: "#140a9a" } }}
                        className="tab-list"
                      >
                        {tabPanels.map((tab, index) => (
                          <Tab
                            key={index}
                            value={tab.route}
                            label={tab.label}
                            className={tab.className}
                            onClick={() => {
                              navigate(tab.route);
                            }}
                            disabled={tab.disabled}
                          />
                        ))}
                      </Tabs>
                    </Box>

                    <div role="tabpanel">
                      <Outlet
                        context={{
                          refreshMachineData,
                        }}
                      />
                    </div>
                  </Box>
                </div>
              </div>
              <Footer className="d-none-sp" />
            </div>
          </div>
        </div>
      </div>
      <Footer className="d-none d-block-sp" />
    </>
  );
}
